import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

const ARIAONE_LOGO = "/ariaone_logo_black.png";
const LUMINA_LOGO = "https://customer-assets.emergentagent.com/job_revenue-cycle-1/artifacts/rms1wglh_lumina_logo_black.png";
const REVQ_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/zakm44og_revQ_logo_black.png";
const ECHO_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/r0ngtnwn_echo_logo_black.svg";

const APP_META = {
  lumina: { logo: LUMINA_LOGO, name: 'Lumina', href: '/lumina', tag: 'Clinical Intelligence' },
  revq: { logo: REVQ_LOGO, name: 'RevQ', href: '/revq', tag: 'Billing Intelligence' },
  echo: { logo: ECHO_LOGO, name: 'Echo', href: '/echo', tag: 'Front Office Operations' },
};

const SetupSuccessPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const selectedApps = state?.apps || ['lumina'];
  const [phase, setPhase] = useState('loading');

  useEffect(() => {
    const timer = setTimeout(() => setPhase('done'), 2500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4" data-testid="setup-success-page">
      <div className="w-full max-w-[480px] space-y-8">
        {/* Logo */}
        <div className="text-center">
          <img src={ARIAONE_LOGO} alt="AriaOne" className="h-8 mx-auto mb-2" data-testid="success-logo" />
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          {phase === 'loading' ? (
            <div className="py-8 space-y-4" data-testid="success-loading">
              <div className="relative h-16 w-16 mx-auto">
                <div className="absolute inset-0 rounded-full border-4 border-slate-100" />
                <div className="absolute inset-0 rounded-full border-4 border-t-slate-700 animate-spin" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Setting up your workspace...</p>
                <p className="text-xs text-slate-400 mt-1">Configuring {selectedApps.length} application{selectedApps.length > 1 ? 's' : ''}</p>
              </div>
            </div>
          ) : (
            <div className="py-4 space-y-6" data-testid="success-complete">
              <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">You're all set!</h1>
                <p className="text-sm text-slate-500 mt-1">Your Aria One workspace is ready to go.</p>
              </div>

              <div className="space-y-2">
                {selectedApps.map(key => {
                  const app = APP_META[key];
                  if (!app) return null;
                  return (
                    <button key={key} onClick={() => navigate(app.href)}
                      className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-left"
                      data-testid={`launch-${key}`}>
                      <img src={app.logo} alt={app.name} className="h-4 w-auto" />
                      <div className="flex-1">
                        <p className="text-xs text-slate-400">{app.tag}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-slate-300" />
                    </button>
                  );
                })}
              </div>

              <Button onClick={() => navigate('/landing')}
                className="w-full h-11 text-sm font-medium bg-slate-900 hover:bg-slate-800"
                data-testid="go-to-dashboard-btn">
                <Sparkles className="h-4 w-4 mr-1.5" /> Go to Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupSuccessPage;
